import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  ArrowLeft,
  Plus,
  GripVertical,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Loader2,
  Trash2,
  Copy,
  FileText,
  FileUp,
  ExternalLink,
  Keyboard,
  PanelRightOpen,
  PanelRightClose,
  Library,
  CalendarDays,
  Check,
  Send,
  CircleOff,
  RotateCcw,
  Users,
  X,
  ChartNetwork,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  Quote,
  useUpdateQuoteVersion,
  useUpdateQuote,
  useAcceptQuoteVersion,
  useDeleteQuoteVersion,
  useUnacceptQuoteVersion,
  useSetQuoteVersionStatus,
} from "@/hooks/useQuotes";
import { useCompanies } from "@/hooks/useCompanies";
import { usePeople } from "@/hooks/usePeople";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { InlineCompanyForm } from "@/components/forms/InlineCompanyForm";
import { InlinePersonForm } from "@/components/forms/InlinePersonForm";
import {
  useQuoteVersionGroupsWithItems,
  useCreateQuoteGroup,
  useUpdateQuoteGroup,
  useDeleteQuoteGroup,
  useDuplicateQuoteGroup,
  useCreateQuoteLineItem,
  useUpdateQuoteLineItem,
  useDeleteQuoteLineItem,
  useReorderQuoteLineItems,
  useQuoteVersionDeductionItems,
  calculateQuoteGroupsTotals,
  calculateLineItemTotal,
  QuoteGroupWithItems,
  QuoteLineItemEntity,
} from "@/hooks/useQuoteEntities";
import { useQueryClient } from "@tanstack/react-query";
import { useSyncedField } from "@/hooks/useRealtime";
import { useServerEvents } from "@/hooks/useServerEvents";
import {
  FinanceLineItemRow,
  type FinanceLineItemRowProps,
} from "./FinanceLineItemRow";
import { QuoteTotalsSummary } from "./QuoteTotalsSummary";
import {
  QuotePDFDocument,
  generateQuotePDFBlob,
  uploadPDFToS3,
  type QuotePDFData,
} from "@/lib/quote-pdf-generator";
import {
  useQuoteLineItemTemplates,
  resolveTemplateName,
  QuoteLineItemTemplate,
} from "@/hooks/useQuoteLineItemTemplates";
import {
  useQuoteGroupTemplates,
  QuoteGroupTemplate,
} from "@/hooks/useQuoteGroupTemplates";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getAutoFillFactor } from "@/lib/equipment-factor-scale";
import { formatDate } from "@/lib/date-utils";
import { useAppSetting } from "@/hooks/useAppSettings";
import { useIsMobile } from "@/hooks/use-mobile";
import type { CustomerContact } from "@/types/finance-entities";
import { CopyQuoteVersionDialog } from "./CopyQuoteVersionDialog";
import { QuoteSankeyDialog } from "./QuoteSankey";

/** Detect iOS devices */
function useIsIOS() {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
  }, []);

  return isIOS;
}

/** generateUUID() fallback for insecure contexts (Firefox over HTTP) */
function generateUUID(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
}

interface QuoteVersionEditorProps {
  versionId: string;
  quote: Quote;
  projectId: string;
  projectNumber?: string;
  projectLanguage: string;
  projectConditions?: string | null;
  eventStartDate?: string;
  eventEndDate?: string;
  planningStartDate?: string;
  planningEndDate?: string;
  customerContact: CustomerContact;
  projectCustomerName?: string;
  onBack: () => void;
  onCreateVersion?: (duplicateFromVersionId?: string) => void;
}

// Extracted group card component with local state for debounced name save
const COMPACT_BREAKPOINT = 1200;
const PREVIEW_BREAKPOINT = 1400;

function useIsCompact() {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COMPACT_BREAKPOINT - 1}px)`);
    const onChange = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    mql.addEventListener("change", onChange);
    setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isCompact;
}

function useShowPreview() {
  const isWide = window.innerWidth >= PREVIEW_BREAKPOINT;
  const [show, setShow] = useState(isWide);
  // Track whether user has manually toggled (null = follow breakpoint)
  const manualOverride = useRef<boolean | null>(null);

  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${PREVIEW_BREAKPOINT}px)`);
    const onChange = () => {
      if (manualOverride.current === null) {
        setShow(window.innerWidth >= PREVIEW_BREAKPOINT);
      }
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setShow((prev) => {
      manualOverride.current = !prev;
      return !prev;
    });
  }, []);

  return { show, toggle };
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

function usePdfPreviewUrl(data: QuotePDFData | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!data) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error("PDF generation timed out")),
          ms,
        );
        promise.then(
          (val) => {
            clearTimeout(timer);
            resolve(val);
          },
          (err) => {
            clearTimeout(timer);
            reject(err);
          },
        );
      });
    }

    async function generate(attempts: number) {
      for (let i = 0; i < attempts; i++) {
        if (cancelled) return;
        try {
          console.log(
            `[PDF] attempt ${i + 1}: starting render (groups=${data!.content.groups.length}, items=${data!.content.groups.reduce((s, g) => s + g.items.length, 0)})`,
          );
          const blobPromise = pdf(<QuotePDFDocument data={data!} />).toBlob();
          blobPromise.catch((renderErr: unknown) => {
            console.error("[PDF] toBlob() rejected:", renderErr);
          });
          const blob = await withTimeout(blobPromise, 10000);
          if (cancelled) return;
          if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
          const newUrl = URL.createObjectURL(blob);
          prevUrlRef.current = newUrl;
          setUrl(newUrl);
          setLoading(false);
          console.log("[PDF] render succeeded");
          return;
        } catch (err) {
          console.error(`PDF preview attempt ${i + 1} failed:`, err);
          if (i === attempts - 1) {
            if (cancelled) return;
            setLoading(false);
            setError(
              (err as Error)?.message || "Failed to generate PDF preview",
            );
          }
        }
      }
    }

    generate(3);

    return () => {
      cancelled = true;
    };
  }, [data]);

  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
    };
  }, []);

  return { url, loading, error };
}

function SortableLineItemRow(props: FinanceLineItemRowProps & { id: string }) {
  const { id, ...rest } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : undefined,
    ...rest.style,
  };

  return (
    <FinanceLineItemRow
      ref={setNodeRef}
      {...rest}
      style={style}
      dragHandleProps={{ ...attributes, ...listeners }}
    />
  );
}

function QuoteGroupCard({
  group,
  allGroups,
  isCollapsed,
  onToggleCollapse,
  onAddGroupAfter,
  onAddGroupBefore,
  onNewGroupAfter,
  autoFocus,
  templates,
  groupTemplates,
  projectLanguage,
  onMoveItemUp,
  onMoveItemDown,
  onMoveItemToGroup,
  onItemDragEnd,
  eventStartDate,
  eventEndDate,
  readonly,
}: {
  group: QuoteGroupWithItems;
  allGroups: QuoteGroupWithItems[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onAddGroupAfter: () => void;
  onAddGroupBefore: () => void;
  onNewGroupAfter: () => void;
  autoFocus?: boolean;
  templates: QuoteLineItemTemplate[];
  groupTemplates: QuoteGroupTemplate[];
  projectLanguage: string;
  onMoveItemUp: (itemId: string, groupId: string) => void;
  onMoveItemDown: (itemId: string, groupId: string) => void;
  onMoveItemToGroup: (
    itemId: string,
    sourceGroupId: string,
    targetGroupId: string,
  ) => void;
  onItemDragEnd: (groupId: string, event: DragEndEvent) => void;
  eventStartDate?: string;
  eventEndDate?: string;
  readonly?: boolean;
}) {
  const isCompact = useIsCompact();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });
  const updateGroup = useUpdateQuoteGroup();
  const deleteGroup = useDeleteQuoteGroup();
  const duplicateGroup = useDuplicateQuoteGroup();
  const createLineItem = useCreateQuoteLineItem();
  const updateLineItem = useUpdateQuoteLineItem();
  const deleteLineItem = useDeleteQuoteLineItem();

  // Item-level DnD
  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const itemIds = useMemo(() => group.items.map((i) => i.id), [group.items]);
  const [activeItem, setActiveItem] = useState<QuoteLineItemEntity | null>(
    null,
  );

  const handleItemDragStart = useCallback(
    (event: DragStartEvent) => {
      const item = group.items.find((i) => i.id === event.active.id);
      if (item) setActiveItem(item);
    },
    [group.items],
  );

  const handleItemDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveItem(null);
      onItemDragEnd(group.id, event);
    },
    [group.id, onItemDragEnd],
  );

  const otherGroups = useMemo(
    () =>
      allGroups
        .filter((g) => g.id !== group.id)
        .map((g) => ({ id: g.id, name: g.name })),
    [allGroups, group.id],
  );

  const groupNameField = useSyncedField({
    serverValue: group.name,
    onSave: useCallback(
      (name: string) => {
        updateGroup.mutate({ id: group.id, updates: { name } });
      },
      [group.id, updateGroup],
    ),
  });
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus and select the name input for newly created groups
  useEffect(() => {
    if (autoFocus && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [autoFocus]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleOptionalChange = (checked: boolean) => {
    updateGroup.mutate({ id: group.id, updates: { is_optional: checked } });
  };

  const handleApplyProjectDiscountChange = (checked: boolean) => {
    updateGroup.mutate({
      id: group.id,
      updates: { apply_project_discount: checked },
    });
  };

  const handleDeleteGroup = () => {
    deleteGroup.mutate(group.id);
  };

  const [newlyCreatedItemId, setNewlyCreatedItemId] = useState<string | null>(
    null,
  );

  const handleAddLineItem = () => {
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    const lastItem = group.items[group.items.length - 1];
    const defaultType = lastItem?.line_item_type ?? "generic";
    createLineItem.mutate({
      quoteGroupId: group.id,
      quoteVersionId: group.quote_version_id,
      displayOrder: group.items.length,
      optimisticId,
      lineItemType: defaultType,
    });
  };

  const handleAddTitle = () => {
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    createLineItem.mutate({
      quoteGroupId: group.id,
      quoteVersionId: group.quote_version_id,
      name: "",
      amount: 0,
      factor: 0,
      unitPrice: 0,
      discount: 0,
      isTitle: true,
      displayOrder: group.items.length,
      optimisticId,
    });
  };

  const handleAddLineItemBefore = (itemIndex: number) => {
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    // Shift existing items at and after this position
    group.items.forEach((it, i) => {
      if (i >= itemIndex) {
        updateLineItem.mutate({
          id: it.id,
          updates: { display_order: it.display_order + 1 },
        });
      }
    });
    createLineItem.mutate({
      quoteGroupId: group.id,
      quoteVersionId: group.quote_version_id,
      displayOrder: itemIndex,
      optimisticId,
    });
  };

  const handleAddTitleBefore = (itemIndex: number) => {
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    // Shift existing items at and after this position
    group.items.forEach((it, i) => {
      if (i >= itemIndex) {
        updateLineItem.mutate({
          id: it.id,
          updates: { display_order: it.display_order + 1 },
        });
      }
    });
    createLineItem.mutate({
      quoteGroupId: group.id,
      quoteVersionId: group.quote_version_id,
      name: "",
      amount: 0,
      factor: 0,
      unitPrice: 0,
      discount: 0,
      isTitle: true,
      displayOrder: itemIndex,
      optimisticId,
    });
  };

  const handleUpdateLineItem = (
    itemId: string,
    updates: Partial<{
      name: string;
      amount: number;
      factor: number;
      unit_price: number;
      discount: number;
      notes: string;
      line_item_type: "generic" | "equipment" | "crew";
    }>,
  ) => {
    updateLineItem.mutate({ id: itemId, updates });
  };

  const handleGroupNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
      e.preventDefault();
      groupNameField.save();
      onNewGroupAfter();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      groupNameField.save();
      handleAddLineItem();
    }
  };

  const handleDeleteLineItem = (itemId: string) => {
    deleteLineItem.mutate({
      id: itemId,
      quoteVersionId: group.quote_version_id,
    });
  };

  const handleAddFromTemplate = (template: QuoteLineItemTemplate) => {
    const autoFactor =
      getAutoFillFactor(
        template.line_item_type,
        eventStartDate,
        eventEndDate,
      ) ?? 1;
    createLineItem.mutate({
      quoteGroupId: group.id,
      name: resolveTemplateName(template.name, projectLanguage),
      amount: template.default_amount,
      factor: autoFactor,
      unitPrice: template.default_unit_price,
      lineItemType: template.line_item_type,
      displayOrder: group.items.length,
    });
  };

  // Group name autocomplete state
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const selectingGroupRef = useRef(false);

  const filteredGroupTemplates = useMemo(() => {
    if (!groupNameField.value.trim() || groupTemplates.length === 0) return [];
    const query = groupNameField.value.toLowerCase();
    return groupTemplates.filter((t) => {
      const resolved = resolveTemplateName(
        t.name,
        projectLanguage,
      ).toLowerCase();
      return resolved.includes(query) && resolved !== query;
    });
  }, [groupNameField.value, groupTemplates, projectLanguage]);

  const isGroupPopoverOpen =
    showGroupSuggestions && filteredGroupTemplates.length > 0;

  const handleSelectGroupTemplate = useCallback(
    (template: QuoteGroupTemplate) => {
      const resolvedName = resolveTemplateName(template.name, projectLanguage);
      groupNameField.inputProps.onChange({
        target: { value: resolvedName },
      } as React.ChangeEvent<HTMLInputElement>);
      setShowGroupSuggestions(false);
      selectingGroupRef.current = false;

      // Apply template: update group name and optional flag
      updateGroup.mutate({
        id: group.id,
        updates: {
          name: resolvedName,
          is_optional: template.default_is_optional,
          apply_project_discount: template.default_apply_project_discount,
        },
      });

      // Create line items from template items, appended in template order
      for (const [index, templateItem] of template.items.entries()) {
        const displayOrder = group.items.length + index;
        if (templateItem.is_title) {
          createLineItem.mutate({
            quoteGroupId: group.id,
            quoteVersionId: group.quote_version_id,
            name: templateItem.name
              ? resolveTemplateName(templateItem.name, projectLanguage)
              : "",
            amount: 0,
            factor: 0,
            unitPrice: 0,
            isTitle: true,
            displayOrder,
          });
        } else if (templateItem.template) {
          const autoFactor =
            getAutoFillFactor(
              templateItem.template.line_item_type,
              eventStartDate,
              eventEndDate,
            ) ?? 1;
          createLineItem.mutate({
            quoteGroupId: group.id,
            quoteVersionId: group.quote_version_id,
            name: resolveTemplateName(
              templateItem.template.name,
              projectLanguage,
            ),
            amount: templateItem.default_amount,
            factor: autoFactor,
            unitPrice: templateItem.template.default_unit_price,
            lineItemType: templateItem.template.line_item_type,
            displayOrder,
          });
        }
      }
    },
    [
      projectLanguage,
      group.id,
      group.quote_version_id,
      group.items.length,
      updateGroup,
      createLineItem,
      groupNameField,
      eventStartDate,
      eventEndDate,
    ],
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={group.is_optional ? "border-dashed" : ""}
      data-testid={`quote-group-${group.id}`}
    >
      <Collapsible open={!isCollapsed} onOpenChange={onToggleCollapse}>
        <CardHeader className="py-3">
          {isCompact ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <Popover
                  open={isGroupPopoverOpen}
                  onOpenChange={setShowGroupSuggestions}
                >
                  <PopoverAnchor asChild>
                    <Input
                      ref={nameInputRef}
                      {...groupNameField.inputProps}
                      onChange={(e) => {
                        groupNameField.inputProps.onChange(e);
                        setShowGroupSuggestions(true);
                      }}
                      onBlur={(e) => {
                        if (selectingGroupRef.current) return;
                        setShowGroupSuggestions(false);
                        groupNameField.inputProps.onBlur();
                      }}
                      onFocus={() => {
                        if (groupNameField.value.trim())
                          setShowGroupSuggestions(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setShowGroupSuggestions(false);
                          return;
                        }
                        handleGroupNameKeyDown(e);
                        if (!e.defaultPrevented)
                          groupNameField.inputProps.onKeyDown(e);
                      }}
                      className="h-8 text-base font-medium flex-1"
                      autoComplete="off"
                      disabled={readonly}
                      data-testid="quote-group-name"
                    />
                  </PopoverAnchor>
                  <PopoverContent
                    className="p-1 w-[--radix-popover-trigger-width]"
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                    onFocusOutside={(e) => e.preventDefault()}
                  >
                    {filteredGroupTemplates.map((template) => (
                      <div
                        key={template.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectingGroupRef.current = true;
                          handleSelectGroupTemplate(template);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          selectingGroupRef.current = true;
                          handleSelectGroupTemplate(template);
                        }}
                        className="flex justify-between items-center w-full rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                      >
                        <span>
                          {resolveTemplateName(template.name, projectLanguage)}
                        </span>
                        {template.items.length > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {template.items.length} items
                          </span>
                        )}
                      </div>
                    ))}
                  </PopoverContent>
                </Popover>
                {!readonly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          duplicateGroup.mutate({ groupId: group.id, group })
                        }
                      >
                        Duplicate group
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onAddGroupBefore}>
                        Add group before
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onAddGroupAfter}>
                        Add group after
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        Delete group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className="flex items-center gap-2 pl-8">
                <Switch
                  id={`optional-${group.id}`}
                  checked={group.is_optional}
                  onCheckedChange={handleOptionalChange}
                  disabled={readonly}
                />
                <Label
                  htmlFor={`optional-${group.id}`}
                  className="text-sm font-normal"
                >
                  Optional
                </Label>
                <Switch
                  id={`apply-project-discount-${group.id}`}
                  checked={group.apply_project_discount}
                  onCheckedChange={handleApplyProjectDiscountChange}
                  disabled={readonly}
                  data-testid="quote-group-apply-discount"
                />
                <Label
                  htmlFor={`apply-project-discount-${group.id}`}
                  className="text-sm font-normal"
                >
                  Apply discount
                </Label>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex min-w-0 items-center gap-3">
                {!readonly && (
                  <GripVertical
                    className="h-4 w-4 flex-shrink-0 cursor-grab touch-none text-muted-foreground"
                    {...listeners}
                  />
                )}
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <Popover
                  open={isGroupPopoverOpen}
                  onOpenChange={setShowGroupSuggestions}
                >
                  <PopoverAnchor asChild>
                    <Input
                      ref={nameInputRef}
                      {...groupNameField.inputProps}
                      onChange={(e) => {
                        groupNameField.inputProps.onChange(e);
                        setShowGroupSuggestions(true);
                      }}
                      onBlur={(e) => {
                        if (selectingGroupRef.current) return;
                        setShowGroupSuggestions(false);
                        groupNameField.inputProps.onBlur();
                      }}
                      onFocus={() => {
                        if (groupNameField.value.trim())
                          setShowGroupSuggestions(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setShowGroupSuggestions(false);
                          return;
                        }
                        handleGroupNameKeyDown(e);
                        if (!e.defaultPrevented)
                          groupNameField.inputProps.onKeyDown(e);
                      }}
                      className="h-8 max-w-xl flex-1 text-base font-medium"
                      autoComplete="off"
                      disabled={readonly}
                      data-testid="quote-group-name"
                    />
                  </PopoverAnchor>
                  <PopoverContent
                    className="p-1 w-[--radix-popover-trigger-width]"
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                    onFocusOutside={(e) => e.preventDefault()}
                  >
                    {filteredGroupTemplates.map((template) => (
                      <div
                        key={template.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectingGroupRef.current = true;
                          handleSelectGroupTemplate(template);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          selectingGroupRef.current = true;
                          handleSelectGroupTemplate(template);
                        }}
                        className="flex justify-between items-center w-full rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                      >
                        <span>
                          {resolveTemplateName(template.name, projectLanguage)}
                        </span>
                        {template.items.length > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {template.items.length} items
                          </span>
                        )}
                      </div>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
              {!readonly && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pl-16">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-normal">Optional</Label>
                    <Switch
                      checked={group.is_optional}
                      onCheckedChange={handleOptionalChange}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-normal">
                      Apply discount
                    </Label>
                    <Switch
                      checked={group.apply_project_discount}
                      onCheckedChange={handleApplyProjectDiscountChange}
                      data-testid="quote-group-apply-discount"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground"
                    onClick={() =>
                      duplicateGroup.mutate({ groupId: group.id, group })
                    }
                    title="Duplicate group"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={onAddGroupBefore}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Before
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={onAddGroupAfter}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    After
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                    title="Delete group"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {isCompact ? (
              <div>
                {group.items.map((item, itemIndex) => (
                  <FinanceLineItemRow
                    key={item.id}
                    item={item}
                    onUpdate={(updates) =>
                      handleUpdateLineItem(item.id, updates)
                    }
                    onDelete={() => handleDeleteLineItem(item.id)}
                    onNextItem={
                      itemIndex < group.items.length - 1
                        ? () => {
                            const nextId = group.items[itemIndex + 1].id;
                            const el = document.querySelector<HTMLInputElement>(
                              `[data-item-name="${nextId}"]`,
                            );
                            el?.focus();
                            el?.select();
                          }
                        : handleAddLineItem
                    }
                    onNewGroup={onNewGroupAfter}
                    autoFocus={item.id === newlyCreatedItemId}
                    templates={templates}
                    projectLanguage={projectLanguage}
                    onMoveUp={() => onMoveItemUp(item.id, group.id)}
                    onMoveDown={() => onMoveItemDown(item.id, group.id)}
                    onMoveToGroup={(targetGroupId) =>
                      onMoveItemToGroup(item.id, group.id, targetGroupId)
                    }
                    otherGroups={otherGroups}
                    isFirst={itemIndex === 0}
                    isLast={itemIndex === group.items.length - 1}
                    eventStartDate={eventStartDate}
                    eventEndDate={eventEndDate}
                    onAddItemBefore={() => handleAddLineItemBefore(itemIndex)}
                    onAddTitleBefore={() => handleAddTitleBefore(itemIndex)}
                    readonly={readonly}
                  />
                ))}
                {!readonly && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={handleAddLineItem}
                      data-testid="add-line-item-button"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add line item
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={handleAddTitle}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add title
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <DndContext
                sensors={itemSensors}
                collisionDetection={closestCenter}
                onDragStart={handleItemDragStart}
                onDragEnd={handleItemDragEnd}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 w-8"></th>
                        <th
                          className="pb-2 font-medium"
                          style={{ width: 36 }}
                        ></th>
                        <th className="pb-2 font-medium w-[33%]">Name</th>
                        <th className="pb-2 font-medium text-right w-[10%]">
                          Amount
                        </th>
                        <th className="pb-2 font-medium text-right w-[10%]">
                          Factor
                        </th>
                        <th className="pb-2 font-medium text-right w-[15%]">
                          Unit Price
                        </th>
                        <th className="pb-2 font-medium text-right w-[10%]">
                          Discount
                        </th>
                        <th className="pb-2 font-medium text-right w-[15%]">
                          Total
                        </th>
                        <th className="pb-2 w-[70px]"></th>
                      </tr>
                    </thead>
                    <SortableContext
                      items={itemIds}
                      strategy={verticalListSortingStrategy}
                    >
                      <tbody>
                        {group.items.map((item, itemIndex) => (
                          <SortableLineItemRow
                            key={item.id}
                            id={item.id}
                            item={item}
                            onUpdate={(updates) =>
                              handleUpdateLineItem(item.id, updates)
                            }
                            onDelete={() => handleDeleteLineItem(item.id)}
                            onNextItem={
                              itemIndex < group.items.length - 1
                                ? () => {
                                    const nextId =
                                      group.items[itemIndex + 1].id;
                                    const el =
                                      document.querySelector<HTMLInputElement>(
                                        `[data-item-name="${nextId}"]`,
                                      );
                                    el?.focus();
                                    el?.select();
                                  }
                                : handleAddLineItem
                            }
                            onNewGroup={onNewGroupAfter}
                            autoFocus={item.id === newlyCreatedItemId}
                            templates={templates}
                            projectLanguage={projectLanguage}
                            eventStartDate={eventStartDate}
                            eventEndDate={eventEndDate}
                            onMoveUp={() => onMoveItemUp(item.id, group.id)}
                            onMoveDown={() => onMoveItemDown(item.id, group.id)}
                            onMoveToGroup={(targetGroupId) =>
                              onMoveItemToGroup(
                                item.id,
                                group.id,
                                targetGroupId,
                              )
                            }
                            otherGroups={otherGroups}
                            isFirst={itemIndex === 0}
                            isLast={itemIndex === group.items.length - 1}
                            onAddItemBefore={() =>
                              handleAddLineItemBefore(itemIndex)
                            }
                            onAddTitleBefore={() =>
                              handleAddTitleBefore(itemIndex)
                            }
                            readonly={readonly}
                          />
                        ))}
                        {!readonly && (
                          <tr className="border-b last:border-0">
                            <td colSpan={9} className="py-2">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground"
                                  onClick={handleAddLineItem}
                                  data-testid="add-line-item-button"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add line item
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground"
                                  onClick={handleAddTitle}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add title
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </SortableContext>
                  </table>
                </div>
                <DragOverlay>
                  {activeItem ? (
                    <div className="bg-background border rounded-md px-3 py-2 shadow-lg text-sm font-medium">
                      {activeItem.name || "Unnamed item"}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{groupNameField.value}"? All line
              items in this group will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export function QuoteVersionEditor({
  versionId,
  quote,
  projectId,
  projectNumber,
  projectLanguage,
  projectConditions,
  eventStartDate,
  eventEndDate,
  planningStartDate,
  planningEndDate,
  customerContact,
  projectCustomerName,
  onBack,
  onCreateVersion,
}: QuoteVersionEditorProps) {
  const queryClient = useQueryClient();
  const updateVersion = useUpdateQuoteVersion();
  const updateQuote = useUpdateQuote();
  const acceptVersion = useAcceptQuoteVersion();
  const deleteVersion = useDeleteQuoteVersion();
  const unacceptVersion = useUnacceptQuoteVersion();
  const setVersionStatus = useSetQuoteVersionStatus();
  const createGroup = useCreateQuoteGroup();
  const version = quote.versions?.find((v) => v.id === versionId);
  const versionStatus =
    version?.status || (version?.is_accepted ? "accepted" : "draft");
  const isReadOnly = versionStatus !== "draft";
  const { data: groups = [], isLoading: groupsLoading } =
    useQuoteVersionGroupsWithItems(versionId);
  const { data: deductionItems = [] } =
    useQuoteVersionDeductionItems(versionId);

  // SSE: invalidate quote versions when settings change from another client
  useServerEvents(
    versionId ? `quote:version:${versionId}` : null,
    useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      queryClient.invalidateQueries({ queryKey: ["quote"] });
    }, [queryClient]),
  );
  const { show: showPreview, toggle: togglePreview } = useShowPreview();
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();
  const { data: templates = [] } = useQuoteLineItemTemplates();
  const { data: groupTemplates = [] } = useQuoteGroupTemplates();
  const createLineItem = useCreateQuoteLineItem();

  // Customer override
  const { data: companies } = useCompanies();
  const { data: allContacts } = usePeople("contact");
  const hasCustomerOverride = !!quote.customer_id;
  const overrideCustomerId = quote.customer_id ?? null;
  const [showOverrideForm, setShowOverrideForm] = useState(false);

  const customerOptions = useMemo(
    () =>
      companies
        ?.filter((c) => c.types?.includes("customer"))
        .map((c) => ({ value: c.id, label: c.name })) ?? [],
    [companies],
  );

  const contactOptions = useMemo(() => {
    const filtered = overrideCustomerId
      ? (allContacts?.filter((p) => p.company_id === overrideCustomerId) ?? [])
      : (allContacts ?? []);
    return filtered.map((p) => ({
      value: p.id,
      label: `${p.first_name} ${p.last_name}`,
      subtitle: p.companies?.name,
    }));
  }, [allContacts, overrideCustomerId]);

  const effectiveCustomerContact = useMemo<CustomerContact>(
    () => ({
      customerName: hasCustomerOverride
        ? quote.customer?.name
        : customerContact.customerName,
      customerAddress: hasCustomerOverride
        ? (quote.customer?.invoice_address ?? undefined)
        : customerContact.customerAddress,
      customerReference: hasCustomerOverride
        ? (quote.customer?.number ?? undefined)
        : customerContact.customerReference,
      contactName: quote.contact_id
        ? quote.contact
          ? `${quote.contact.first_name} ${quote.contact.last_name}`
          : undefined
        : customerContact.contactName,
      accountManagerName: customerContact.accountManagerName,
    }),
    [
      customerContact,
      hasCustomerOverride,
      quote.contact,
      quote.contact_id,
      quote.customer,
    ],
  );

  const [internalName, setInternalName] = useState("");
  const [quoteDate, setQuoteDate] = useState("");
  const [validDays, setValidDays] = useState(20);
  const offerTitleField = useSyncedField({
    serverValue: quote.name,
    onSave: useCallback(
      (value: string) => {
        const name = value.trim();
        if (!name || name === quote.name) return;
        updateQuote.mutate({ id: quote.id, name });
      },
      [quote.id, quote.name, updateQuote],
    ),
  });
  const offerTitle = offerTitleField.value.trim() || quote.name;
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const effectiveQuoteDate = quoteDate || today;
  const validUntil = useMemo(() => {
    const d = new Date(effectiveQuoteDate);
    if (isNaN(d.getTime())) return "";
    d.setDate(d.getDate() + validDays);
    return d.toISOString().split("T")[0];
  }, [effectiveQuoteDate, validDays]);
  const [projectDiscountPercent, setProjectDiscountPercent] = useState(0);
  const [vatRate, setVatRate] = useState<"CH_8_1" | "NONE">("CH_8_1");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );
  const [showCopyVersionDialog, setShowCopyVersionDialog] = useState(false);
  const [showSankey, setShowSankey] = useState(false);
  const [showDeleteVersionConfirm, setShowDeleteVersionConfirm] =
    useState(false);
  const [newlyCreatedGroupId, setNewlyCreatedGroupId] = useState<string | null>(
    null,
  );
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [generalConditions, setGeneralConditions] = useState("");
  const { data: conditionsTemplates } = useAppSetting<Record<string, string>>(
    "general_conditions_templates",
  );
  const defaultGeneralConditions = useMemo(
    () =>
      projectConditions ||
      conditionsTemplates?.[projectLanguage] ||
      conditionsTemplates?.de ||
      "",
    [projectConditions, conditionsTemplates, projectLanguage],
  );

  const updateGroup = useUpdateQuoteGroup();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const groupIds = useMemo(() => groups.map((g) => g.id), [groups]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = groups.findIndex((g) => g.id === active.id);
      const newIndex = groups.findIndex((g) => g.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // Build the new order
      const reordered = [...groups];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      // Optimistically update the cache
      const queryKey = ["quote-groups-with-items", versionId];
      const updated = reordered.map((g, i) => ({ ...g, display_order: i }));
      queryClient.setQueryData<QuoteGroupWithItems[]>(queryKey, updated);

      // Persist changed display_orders
      reordered.forEach((g, i) => {
        if (g.display_order !== i) {
          updateGroup.mutate({ id: g.id, updates: { display_order: i } });
        }
      });
    },
    [groups, updateGroup, queryClient, versionId],
  );

  const updateLineItem = useUpdateQuoteLineItem();
  const reorderLineItems = useReorderQuoteLineItems();

  const handleItemDragEnd = useCallback(
    (groupId: string, event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const group = groups.find((g) => g.id === groupId);
      if (!group) return;

      const oldIndex = group.items.findIndex((i) => i.id === active.id);
      const newIndex = group.items.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedItems = [...group.items];
      const [moved] = reorderedItems.splice(oldIndex, 1);
      reorderedItems.splice(newIndex, 0, moved);

      // Optimistic update
      const queryKey = ["quote-groups-with-items", versionId];
      const updatedGroups = groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          items: reorderedItems.map((item, i) => ({
            ...item,
            display_order: i,
          })),
        };
      });
      queryClient.setQueryData<QuoteGroupWithItems[]>(queryKey, updatedGroups);

      reorderLineItems.mutate({
        quoteGroupId: groupId,
        orderedIds: reorderedItems.map((item) => item.id),
      });
    },
    [groups, reorderLineItems, queryClient, versionId],
  );

  const handleMoveItemUp = useCallback(
    (itemId: string, groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;
      const idx = group.items.findIndex((i) => i.id === itemId);
      if (idx <= 0) return;

      const reordered = [...group.items];
      [reordered[idx - 1], reordered[idx]] = [
        reordered[idx],
        reordered[idx - 1],
      ];

      const queryKey = ["quote-groups-with-items", versionId];
      const updatedGroups = groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          items: reordered.map((item, i) => ({ ...item, display_order: i })),
        };
      });
      queryClient.setQueryData<QuoteGroupWithItems[]>(queryKey, updatedGroups);

      reorderLineItems.mutate({
        quoteGroupId: groupId,
        orderedIds: reordered.map((item) => item.id),
      });
    },
    [groups, reorderLineItems, queryClient, versionId],
  );

  const handleMoveItemDown = useCallback(
    (itemId: string, groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;
      const idx = group.items.findIndex((i) => i.id === itemId);
      if (idx === -1 || idx >= group.items.length - 1) return;

      const reordered = [...group.items];
      [reordered[idx], reordered[idx + 1]] = [
        reordered[idx + 1],
        reordered[idx],
      ];

      const queryKey = ["quote-groups-with-items", versionId];
      const updatedGroups = groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          items: reordered.map((item, i) => ({ ...item, display_order: i })),
        };
      });
      queryClient.setQueryData<QuoteGroupWithItems[]>(queryKey, updatedGroups);

      reorderLineItems.mutate({
        quoteGroupId: groupId,
        orderedIds: reordered.map((item) => item.id),
      });
    },
    [groups, reorderLineItems, queryClient, versionId],
  );

  const handleMoveItemToGroup = useCallback(
    (itemId: string, sourceGroupId: string, targetGroupId: string) => {
      const sourceGroup = groups.find((g) => g.id === sourceGroupId);
      const targetGroup = groups.find((g) => g.id === targetGroupId);
      if (!sourceGroup || !targetGroup) return;

      const item = sourceGroup.items.find((i) => i.id === itemId);
      if (!item) return;

      const newDisplayOrder = targetGroup.items.length;

      // Optimistic update
      const queryKey = ["quote-groups-with-items", versionId];
      const updatedGroups = groups.map((g) => {
        if (g.id === sourceGroupId) {
          const remaining = g.items.filter((i) => i.id !== itemId);
          return {
            ...g,
            items: remaining.map((i, idx) => ({ ...i, display_order: idx })),
          };
        }
        if (g.id === targetGroupId) {
          return {
            ...g,
            items: [
              ...g.items,
              {
                ...item,
                quote_group_id: targetGroupId,
                display_order: newDisplayOrder,
              },
            ],
          };
        }
        return g;
      });
      queryClient.setQueryData<QuoteGroupWithItems[]>(queryKey, updatedGroups);

      // Persist: move item to new group
      updateLineItem.mutate({
        id: itemId,
        updates: {
          quote_group_id: targetGroupId,
          display_order: newDisplayOrder,
        },
      });

      // Re-order remaining items in source group
      const remaining = sourceGroup.items.filter((i) => i.id !== itemId);
      reorderLineItems.mutate({
        quoteGroupId: sourceGroupId,
        orderedIds: remaining.map((i) => i.id),
      });
    },
    [groups, updateLineItem, reorderLineItems, queryClient, versionId],
  );

  useEffect(() => {
    if (version) {
      setInternalName(version.internal_name || "");
      setQuoteDate(version.quote_date || "");
      setValidDays(version.validity_days ?? 20);
      setProjectDiscountPercent(version.project_discount_percent);
      setVatRate(version.vat_rate as "CH_8_1" | "NONE");
      setGeneralConditions(
        version.general_conditions ?? defaultGeneralConditions,
      );
    }
  }, [versionId, version, defaultGeneralConditions]);

  const saveField = useCallback(
    (updates: Parameters<typeof updateVersion.mutate>[0]["updates"]) => {
      updateVersion.mutate({ id: versionId, updates });
    },
    [versionId, updateVersion],
  );
  const saveGeneralConditions = useCallback(() => {
    const savedGeneralConditions = version?.general_conditions ?? null;
    const nextGeneralConditions =
      generalConditions === defaultGeneralConditions
        ? null
        : generalConditions || null;

    if (nextGeneralConditions === savedGeneralConditions) return;
    saveField({ general_conditions: nextGeneralConditions });
  }, [
    defaultGeneralConditions,
    generalConditions,
    saveField,
    version?.general_conditions,
  ]);

  // Build PDF data from current editor state
  const versionLabel = version ? String(version.version_number) : "";
  const quoteNumber = [projectNumber, quote.quote_number, versionLabel]
    .filter(Boolean)
    .join("-");

  const pdfData: QuotePDFData = useMemo(
    () => ({
      quoteName: offerTitle,
      quoteNumber,
      versionLabel,
      quoteDate: effectiveQuoteDate,
      validUntil,
      projectDiscountPercent,
      vatRate,
      language: projectLanguage,
      content: {
        groups: groups.map((group) => ({
          id: group.id,
          name: group.name,
          order: group.display_order,
          optional: group.is_optional,
          applyProjectDiscount: group.apply_project_discount,
          items: group.items.map((item) => ({
            id: item.id,
            name: item.name,
            amount: item.amount,
            factor: item.factor,
            unitPrice: item.unit_price,
            discount: item.discount,
            total: item.is_title
              ? 0
              : calculateLineItemTotal(
                  item.amount,
                  item.factor,
                  item.unit_price,
                  item.discount,
                ),
            notes: item.notes,
            isTitle: item.is_title,
            externalNote: item.external_note,
          })),
        })),
      },
      deductions: deductionItems.map((item) => ({
        description: item.description,
        amount: item.amount,
      })),
      ...effectiveCustomerContact,
      eventStartDate,
      eventEndDate,
      planningStartDate,
      planningEndDate,
      generalConditions: generalConditions || undefined,
    }),
    [
      offerTitle,
      quoteNumber,
      versionLabel,
      effectiveQuoteDate,
      validUntil,
      projectDiscountPercent,
      vatRate,
      projectLanguage,
      groups,
      deductionItems,
      effectiveCustomerContact,
      eventStartDate,
      eventEndDate,
      planningStartDate,
      planningEndDate,
      generalConditions,
    ],
  );

  // Debounce PDF data to avoid re-rendering PDF on every keystroke
  const debouncedPdfData = useDebouncedValue(pdfData, 500);
  const pdfPreview = usePdfPreviewUrl(showPreview ? debouncedPdfData : null);

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      // Set quote date to today if not explicitly set
      if (!quoteDate) {
        setQuoteDate(today);
      }

      const blob = await generateQuotePDFBlob(pdfData);
      const fileName = `${quoteNumber}_${offerTitle.replace(/\s+/g, "_")}.pdf`;
      const publicUrl = await uploadPDFToS3(blob, fileName);

      // Save the URL and quote_date (if newly set) to the database
      const updates: Record<string, unknown> = { pdf_url: publicUrl };
      if (!quoteDate) {
        updates.quote_date = today;
      }
      updateVersion.mutate({
        id: versionId,
        updates,
      });

      toast.success("PDF generated and uploaded");
    } catch (error) {
      console.error("PDF generate error:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = () => {
    if (version?.pdf_url) {
      window.open(version.pdf_url, "_blank");
    }
  };

  const addGroup = (afterIndex?: number) => {
    let displayOrder: number;
    if (afterIndex === undefined) {
      displayOrder = groups.length;
    } else if (afterIndex < 0) {
      displayOrder = 0;
    } else {
      displayOrder = afterIndex + 1;
    }
    const optimisticId = generateUUID();
    setNewlyCreatedGroupId(optimisticId);
    createGroup.mutate({
      quoteVersionId: versionId,
      displayOrder,
      optimisticId,
    });
  };

  const addGroupFromTemplate = async (template: QuoteGroupTemplate) => {
    const resolvedGroupName = resolveTemplateName(
      template.name,
      projectLanguage,
    );
    const newGroup = await createGroup.mutateAsync({
      quoteVersionId: versionId,
      name: resolvedGroupName,
      displayOrder: groups.length,
      isOptional: template.default_is_optional,
      applyProjectDiscount: template.default_apply_project_discount,
    });

    for (const [displayOrder, templateItem] of template.items.entries()) {
      if (templateItem.is_title) {
        createLineItem.mutate({
          quoteGroupId: newGroup.id,
          quoteVersionId: versionId,
          name: templateItem.name
            ? resolveTemplateName(templateItem.name, projectLanguage)
            : "",
          amount: 0,
          factor: 0,
          unitPrice: 0,
          isTitle: true,
          displayOrder,
        });
      } else if (templateItem.template) {
        const autoFactor =
          getAutoFillFactor(
            templateItem.template.line_item_type,
            eventStartDate,
            eventEndDate,
          ) ?? 1;
        createLineItem.mutate({
          quoteGroupId: newGroup.id,
          quoteVersionId: versionId,
          name: resolveTemplateName(
            templateItem.template.name,
            projectLanguage,
          ),
          amount: templateItem.default_amount,
          factor: autoFactor,
          unitPrice: templateItem.template.default_unit_price,
          lineItemType: templateItem.template.line_item_type,
          displayOrder,
        });
      }
    }
  };

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const totals = calculateQuoteGroupsTotals(
    groups,
    projectDiscountPercent,
    vatRate,
    deductionItems,
  );

  if (!version) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Version not found</p>
        <Button variant="ghost" onClick={onBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    );
  }

  const editorContent = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            data-testid="version-editor-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold truncate">{offerTitle}</h2>
              {versionStatus === "accepted" && (
                <Badge variant="default" className="bg-green-600 shrink-0">
                  <Check className="h-3 w-3 mr-1" />
                  Accepted
                </Badge>
              )}
              {versionStatus === "sent" && (
                <Badge variant="default" className="bg-blue-600 shrink-0">
                  <Send className="h-3 w-3 mr-1" />
                  Sent
                </Badge>
              )}
              {versionStatus === "rejected" && (
                <Badge variant="destructive" className="shrink-0">
                  <CircleOff className="h-3 w-3 mr-1" />
                  Rejected
                </Badge>
              )}
              {updateVersion.isPending && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Version: {versionLabel}
              {internalName ? ` – ${internalName}` : ""}
            </p>
            {isReadOnly ? (
              <p className="text-xs text-muted-foreground mt-1">
                This version is locked because it is {versionStatus}.
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            offlineEdit
            variant="outline"
            size="sm"
            onClick={handleGeneratePDF}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileUp className="h-4 w-4 mr-2" />
            )}
            Generate PDF
          </Button>
          {version.pdf_url && (
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Download
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={togglePreview}
            title={showPreview ? "Hide PDF preview" : "Show PDF preview"}
          >
            {showPreview ? (
              <PanelRightClose className="h-4 w-4 mr-2" />
            ) : (
              <PanelRightOpen className="h-4 w-4 mr-2" />
            )}
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={() => setShowSankey(true)}
                data-testid="show-quote-sankey"
              >
                <ChartNetwork className="h-4 w-4 mr-2" />
                Sankey
              </DropdownMenuItem>
              {versionStatus === "draft" && version.pdf_url && (
                <DropdownMenuItem
                  disabled={setVersionStatus.isPending}
                  onClick={() =>
                    setVersionStatus.mutate({ id: versionId, status: "sent" })
                  }
                >
                  <Send className="h-4 w-4 mr-2" />
                  Mark as sent
                </DropdownMenuItem>
              )}
              {version.pdf_url &&
              versionStatus !== "accepted" &&
              versionStatus !== "rejected" ? (
                <DropdownMenuItem
                  disabled={acceptVersion.isPending}
                  onClick={() => acceptVersion.mutate(versionId)}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Accept
                </DropdownMenuItem>
              ) : null}
              {(versionStatus === "draft" || versionStatus === "sent") && (
                <DropdownMenuItem
                  disabled={setVersionStatus.isPending}
                  onClick={() =>
                    setVersionStatus.mutate({
                      id: versionId,
                      status: "rejected",
                    })
                  }
                >
                  <CircleOff className="h-4 w-4 mr-2" />
                  Reject
                </DropdownMenuItem>
              )}
              {versionStatus === "accepted" && (
                <DropdownMenuItem
                  disabled={unacceptVersion.isPending}
                  onClick={() => unacceptVersion.mutate(versionId)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Undo accepted
                </DropdownMenuItem>
              )}
              {versionStatus === "sent" && (
                <DropdownMenuItem
                  disabled={setVersionStatus.isPending}
                  onClick={() =>
                    setVersionStatus.mutate({ id: versionId, status: "draft" })
                  }
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Mark draft
                </DropdownMenuItem>
              )}
              {versionStatus === "rejected" && (
                <DropdownMenuItem
                  disabled={setVersionStatus.isPending}
                  onClick={() =>
                    setVersionStatus.mutate({ id: versionId, status: "draft" })
                  }
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reopen as draft
                </DropdownMenuItem>
              )}
              {onCreateVersion && (
                <DropdownMenuItem onClick={() => onCreateVersion(versionId)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New version
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setShowCopyVersionDialog(true)}>
                <FileText className="h-4 w-4 mr-2" />
                Copy version
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                disabled={versionStatus === "accepted"}
                onClick={() => setShowDeleteVersionConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete version
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Version Settings & Customer */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Version Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Offer Title</Label>
              <Input
                {...offerTitleField.inputProps}
                placeholder="Offer title"
                disabled={isReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label>Internal Name</Label>
              <Input
                placeholder="e.g., Budget reduced"
                value={internalName}
                onChange={(e) => setInternalName(e.target.value)}
                onBlur={() =>
                  saveField({ internal_name: internalName || null })
                }
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  saveField({ internal_name: internalName || null })
                }
                disabled={isReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label>Quote Date</Label>
              <div className="flex items-center gap-1">
                <DatePicker
                  value={quoteDate}
                  onChange={(v) => {
                    setQuoteDate(v);
                    saveField({ quote_date: v || null });
                  }}
                  disabled={isReadOnly}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  title="Set to today"
                  disabled={isReadOnly}
                  onClick={() => {
                    const todayStr = new Date().toISOString().split("T")[0];
                    setQuoteDate(todayStr);
                    saveField({ quote_date: todayStr });
                  }}
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Valid for (days)</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={validDays}
                onChange={(e) => setValidDays(parseInt(e.target.value) || 0)}
                onBlur={() => saveField({ validity_days: validDays })}
                onKeyDown={(e) =>
                  e.key === "Enter" && saveField({ validity_days: validDays })
                }
                disabled={isReadOnly}
              />
              {validUntil && (
                <p className="text-xs text-muted-foreground">
                  Valid until: {formatDate(validUntil)}
                </p>
              )}
            </div>
          </div>

          {/* Customer override */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Customer</Label>
              <span className="text-xs text-muted-foreground">
                {hasCustomerOverride
                  ? `Overriding project customer (${projectCustomerName || "not set"})`
                  : `Using project customer${customerContact.customerName ? `: ${customerContact.customerName}` : ""}`}
              </span>
            </div>
            {hasCustomerOverride || showOverrideForm ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Customer</Label>
                    <EntityCombobox
                      options={customerOptions}
                      value={quote.customer_id}
                      onChange={(value) => {
                        updateQuote.mutate({
                          id: quote.id,
                          customer_id: value || null,
                          contact_id: null,
                        });
                      }}
                      placeholder="Select customer"
                      searchPlaceholder="Search customers..."
                      emptyMessage="No customers found."
                      createLabel="Create new customer"
                      renderCreateForm={({
                        onSuccess,
                        onCancel,
                        initialName,
                      }) => (
                        <InlineCompanyForm
                          onSuccess={onSuccess}
                          onCancel={onCancel}
                          initialName={initialName}
                          defaultTypes={["customer"]}
                        />
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Contact</Label>
                    <EntityCombobox
                      options={contactOptions}
                      value={quote.contact_id}
                      onChange={(value) => {
                        updateQuote.mutate({
                          id: quote.id,
                          contact_id: value || null,
                        });
                      }}
                      placeholder="Select contact"
                      searchPlaceholder="Search contacts..."
                      emptyMessage="No contacts found."
                      createLabel="Create new contact"
                      renderCreateForm={({
                        onSuccess,
                        onCancel,
                        initialName,
                      }) => (
                        <InlinePersonForm
                          onSuccess={onSuccess}
                          onCancel={onCancel}
                          initialName={initialName}
                          defaultType="contact"
                          showTypeSelector={false}
                          companyId={overrideCustomerId}
                        />
                      )}
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => {
                    setShowOverrideForm(false);
                    if (hasCustomerOverride) {
                      updateQuote.mutate({
                        id: quote.id,
                        customer_id: null,
                        contact_id: null,
                      });
                    }
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  Reset to project customer
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOverrideForm(true)}
              >
                <Users className="h-4 w-4 mr-1" />
                Override customer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Groups */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Groups & Line Items</h3>
        </div>

        {groupsLoading ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              <p>Loading groups...</p>
            </CardContent>
          </Card>
        ) : groups.length === 0 ? (
          <div className="space-y-4">
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>No groups yet. Add a group to start building your quote.</p>
              </CardContent>
            </Card>
            {!isReadOnly && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-dashed"
                  onClick={() => addGroup()}
                  data-testid="add-group-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Group
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-dashed"
                      data-testid="add-group-from-template-button"
                    >
                      <Library className="h-4 w-4 mr-1" />
                      From template
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-64 overflow-y-auto"
                  >
                    {groupTemplates.length === 0 ? (
                      <DropdownMenuItem disabled>
                        No group templates yet
                      </DropdownMenuItem>
                    ) : (
                      groupTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => addGroupFromTemplate(template)}
                        >
                          {resolveTemplateName(template.name, projectLanguage)}
                          {template.items.length > 0 && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({template.items.length} items)
                            </span>
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={groupIds}
                strategy={verticalListSortingStrategy}
              >
                {groups.map((group, groupIndex) => (
                  <QuoteGroupCard
                    key={group.id}
                    group={group}
                    allGroups={groups}
                    isCollapsed={collapsedGroups.has(group.id)}
                    onToggleCollapse={() => toggleGroupCollapse(group.id)}
                    onAddGroupAfter={() => addGroup(groupIndex)}
                    onAddGroupBefore={() =>
                      addGroup(groupIndex > 0 ? groupIndex - 1 : -1)
                    }
                    onNewGroupAfter={() => addGroup(groupIndex)}
                    autoFocus={group.id === newlyCreatedGroupId}
                    templates={templates}
                    groupTemplates={groupTemplates}
                    projectLanguage={projectLanguage}
                    onMoveItemUp={handleMoveItemUp}
                    onMoveItemDown={handleMoveItemDown}
                    onMoveItemToGroup={handleMoveItemToGroup}
                    onItemDragEnd={handleItemDragEnd}
                    eventStartDate={eventStartDate}
                    eventEndDate={eventEndDate}
                    readonly={isReadOnly}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {!isReadOnly && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-dashed"
                  onClick={() => addGroup()}
                  data-testid="add-group-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Group
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-dashed"
                      data-testid="add-group-from-template-button"
                    >
                      <Library className="h-4 w-4 mr-1" />
                      From template
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-64 overflow-y-auto"
                  >
                    {groupTemplates.length === 0 ? (
                      <DropdownMenuItem disabled>
                        No group templates yet
                      </DropdownMenuItem>
                    ) : (
                      groupTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => addGroupFromTemplate(template)}
                        >
                          {resolveTemplateName(template.name, projectLanguage)}
                          {template.items.length > 0 && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({template.items.length} items)
                            </span>
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </>
        )}
      </div>

      {/* General Conditions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General Conditions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={10}
            value={generalConditions}
            onChange={(e) => setGeneralConditions(e.target.value)}
            onBlur={saveGeneralConditions}
            placeholder="Enter general conditions..."
            className="font-mono text-xs"
            disabled={isReadOnly}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={isReadOnly}
            onClick={() => {
              setGeneralConditions(defaultGeneralConditions);
              saveField({ general_conditions: null });
            }}
          >
            Reset to template
          </Button>
        </CardContent>
      </Card>

      {/* Totals */}
      <QuoteTotalsSummary
        totals={totals}
        projectDiscountPercent={projectDiscountPercent}
        vatRate={vatRate}
        quoteVersionId={versionId}
        deductionItems={deductionItems}
        onDiscountChange={(value) => setProjectDiscountPercent(value)}
        onDiscountBlur={() =>
          saveField({ project_discount_percent: projectDiscountPercent })
        }
        onVatRateChange={(value) => {
          setVatRate(value);
          saveField({ vat_rate: value });
        }}
        readonly={isReadOnly}
      />

      <CopyQuoteVersionDialog
        open={showCopyVersionDialog}
        onOpenChange={setShowCopyVersionDialog}
        currentProjectId={projectId}
        sourceQuote={quote}
        sourceVersion={version}
      />

      <QuoteSankeyDialog
        open={showSankey}
        onOpenChange={setShowSankey}
        groups={groups}
      />

      <AlertDialog
        open={showDeleteVersionConfirm}
        onOpenChange={setShowDeleteVersionConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Version</AlertDialogTitle>
            <AlertDialogDescription>
              Delete V{version.version_number}? This removes the version and its
              line items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                deleteVersion.mutate(versionId, {
                  onSuccess: () => {
                    setShowDeleteVersionConfirm(false);
                    onBack();
                  },
                });
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Keyboard shortcuts help */}
      <div className="mt-4 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5 mb-2 font-medium">
          <Keyboard className="h-3.5 w-3.5" />
          Keyboard Shortcuts
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <kbd className="font-mono bg-muted px-1 rounded">Enter</kbd>
          <span>Save current field</span>
          <kbd className="font-mono bg-muted px-1 rounded">
            {/Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘" : "Ctrl"} + Enter
          </kbd>
          <span>Save &amp; add new line item</span>
          <kbd className="font-mono bg-muted px-1 rounded">
            {/Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘" : "Ctrl"} + Shift
            + Enter
          </kbd>
          <span>Save &amp; add new group</span>
        </div>
      </div>
    </div>
  );

  if (!showPreview) {
    return editorContent;
  }

  // Preview content – only evaluated when showPreview is true so the PDF
  // preview hook doesn't run unnecessarily.
  const previewContent = (
    <Card className="h-full flex flex-col">
      <CardHeader className="py-3 px-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">PDF Preview</CardTitle>
          {isIOS && pdfPreview.url && (
            <Button
              variant="link"
              size="sm"
              onClick={() => window.open(pdfPreview.url!, "_blank")}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open full PDF
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-0 min-h-0">
        {pdfPreview.loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : pdfPreview.error ? (
          <div className="flex items-center justify-center h-full text-sm text-destructive">
            {pdfPreview.error}
          </div>
        ) : pdfPreview.url ? (
          isIOS ? (
            // iOS Safari only shows first page in iframes; use object tag
            <object
              data={`${pdfPreview.url}#page=1&view=Fit`}
              type="application/pdf"
              className="w-full h-full border-0 rounded-b-lg"
              title="PDF Preview"
            >
              <div className="flex flex-col items-center justify-center h-full p-4 gap-4">
                <p className="text-sm text-muted-foreground text-center">
                  PDF preview not supported in this browser
                </p>
                <Button
                  variant="default"
                  onClick={() => window.open(pdfPreview.url!, "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open PDF in new tab
                </Button>
              </div>
            </object>
          ) : (
            <iframe
              src={pdfPreview.url}
              className="w-full h-full border-0 rounded-b-lg"
              title="PDF Preview"
            />
          )
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            No preview available
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Mobile: show only the preview full-width (editor is hidden)
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold truncate">{offerTitle}</h2>
          </div>
          <Button variant="outline" size="sm" onClick={togglePreview}>
            <PanelRightClose className="h-4 w-4 mr-2" />
            Show Editor
          </Button>
        </div>
        <div className="h-[calc(100vh-12rem)]">{previewContent}</div>
      </div>
    );
  }

  // Desktop: side-by-side resizable panels
  return (
    <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
      {/* Left panel: Editor (independent scroll) */}
      <ResizablePanel defaultSize={60} minSize={30}>
        <div className="h-full overflow-y-auto pr-3">{editorContent}</div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      {/* Right panel: Live PDF Preview (independent scroll) */}
      <ResizablePanel defaultSize={40} minSize={20}>
        <div className="h-full pl-3">{previewContent}</div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
